import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { UserEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { userAccountKeys } from 'Modules/UserAccount/Hooks/userAccountKeys';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import Pagination from 'Views/Components/Pagination/Pagination';
import If from 'Views/Components/If/If';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { Toggle } from 'Views/Components/_HumanWritten/Toggle/Toggle';
import { getUserEntitlements } from 'Services/Api/_HumanWritten/AnytimeTicketService';
import UserEntitlementsList from 'Modules/AnytimeTicket/Components/UserEntitlements/UserEntitlementsList';
import { AccountDetailsTabHeader } from 'Views/Components/_HumanWritten/AccountDetails/AccountDetailsTabHeader';
import AnytimeTicketModal from '../AnytimeTicketModal';
import { anytimeTicketInline, anytimeTicketsLabel } from 'Util/_HumanWritten/anytimeTicketTerm';

interface Props {
	user?: UserEntity;
}

function UserEntitlementsWrapper({ user }: Props) {
	const [searchParams] = useSearchParams();
	const queryClient = useQueryClient();
	const perPage = 4;
	const [pageNo, setPageNo] = useState<number>(0);
	const [checkboxState, setCheckboxState] = useState<boolean>(false);
	const [showPurchaseModal, setShowPurchaseModal] = useState(false);
	const [searchTerm, setSearchTerm] = useState(searchParams.get('search') ?? '');

	// staleTime 0 so every page/filter change refetches (matching the legacy fetch-per-change),
	// while keepPreviousData stops pagination flashing an empty list.
	const { data: entitlements } = useQuery({
		queryKey: userAccountKeys.entitlementPage(user?.id, {
			pageNo,
			showExhaustedOrExpired: checkboxState,
			searchTerm,
		}),
		queryFn: () => getUserEntitlements(user!.id, pageNo * perPage, perPage, checkboxState, searchTerm || undefined),
		enabled: isNotNullOrUndefined(user),
		placeholderData: keepPreviousData,
		staleTime: 0,
	});

	const reload = () => {
		queryClient.invalidateQueries({ queryKey: userAccountKeys.entitlements(user?.id) });
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		setPageNo(0);
	};

	return (
		<>
			<div className="account-page-ferry-vouchers__header-container">
				<AccountDetailsTabHeader headerText={anytimeTicketsLabel()} />
				<div className="credit-voucher__manager-buttons">
					{store.isStaff && (
						<Toggle
							className="credit-voucher__toggle"
							id="entitlement-toggle"
							model={{ showExhaustedOrExpired: checkboxState }}
							modelProperty="showExhaustedOrExpired"
							labelVisible
							labelOn="Used/Expired"
							labelOff="Active"
							onChecked={(_event: any, checked: boolean) => {
								setCheckboxState(checked);
								setPageNo(0);
							}}
						/>
					)}
					<form onSubmit={e => e.preventDefault()}>
						<div className="search-box">
							<input
								placeholder="Search by code"
								className="fh-basic-search"
								autoComplete="off"
								value={searchTerm}
								onChange={handleSearchChange}
							/>
							<If condition={stringNotEmpty(searchTerm)}>
								<Button
									className="clear-search"
									onClick={e => {
										e.preventDefault();
										setSearchTerm('');
										setPageNo(0);
										const params = new URLSearchParams(searchParams);
										params.delete('search');
										window.history.replaceState(
											{},
											'',
											`${window.location.pathname}${params.toString() ? `?${params}` : ''}`,
										);
									}}
									colors={Colors.Black}
									display={Display.Text}
								>
									<Icon name="cross" />
								</Button>
							</If>
						</div>
					</form>
					<Button
						className="add-ferry-voucher__button"
						onClick={() => setShowPurchaseModal(true)}
						icon={{ icon: 'add', iconPos: 'icon-left' }}
					>
						Buy {anytimeTicketInline()}
					</Button>
				</div>
			</div>

			<UserEntitlementsList reload={reload} userEntitlements={entitlements} />

			<AnytimeTicketModal
				isOpen={showPurchaseModal}
				onClose={() => {
					setShowPurchaseModal(false);
					reload();
				}}
				prefillCustomerId={user?.id}
				prefillCustomerName={
					// Names are non-nullable in the model but can be null in the database, which would
					// otherwise prefill the literal string "null null".
					user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : undefined
				}
			/>

			<If condition={isNotNullOrUndefined(entitlements) && entitlements?.count > perPage}>
				<div className="collection-component">
					<section className="collection__pagination new-style">
						<section className="collection__load">
							<Pagination
								perPage={perPage}
								pageNo={pageNo}
								totalRecords={entitlements?.count ?? 0}
								onPageChange={pageNum => {
									setPageNo(pageNum);
								}}
								showGoToPageBox={false}
								showFirstArrow={false}
								showLastArrow={false}
								leftArrowIcon="arrow-left"
								rightArrowIcon="arrow-right"
							/>
						</section>
					</section>
				</div>
			</If>
		</>
	);
}

export default UserEntitlementsWrapper;
